// src/screens/BarterFiEscrow.js
import React, { useEffect, useRef } from 'react';
import './BarterFiEscrow.css';
import backIcon from '../assets/backspace-icon.png';
import { useTranslation, Trans } from 'react-i18next';

// Central config (no default auto-release; seller chooses Estimated Delivery at listing)
const ESCROW = {
  SELLER_CONFIRM_DEADLINE_HOURS: 48,
  DELIVERY_WINDOW_OPTIONS: [3, 7, 14, 21, 30], // days chosen on product/service listing
  COMMISSION_PCT: 10,
  SELLER_PAYOUT_PCT: 90,
};

export default function BarterFiEscrow() {
  const { t } = useTranslation();
  const topRef = useRef(null);
  const s1Ref = useRef(null);
  const s2Ref = useRef(null);
  const s3Ref = useRef(null);
  const s4Ref = useRef(null);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    const map = { smooth: s1Ref, noConfirm: s2Ref, dispute: s3Ref, autoRelease: s4Ref };
    if (map[hash]?.current) map[hash].current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleBack = () => {
    if (window.history.length <= 1) {
      window.location.href = '/home';
    } else {
      window.history.back();
    }
  };

  const deliveryStr = ESCROW.DELIVERY_WINDOW_OPTIONS.join(' / ');

  return (
    <div className="bf-escrow-root" ref={topRef}>
      {/* Top bar */}
      <div className="bf-escrow-topbar">
        <img
          src={backIcon}
          alt={t('escrow.back')}
          className="bf-escrow-back-icon"
          onClick={handleBack}
        />
        <h2 className="bf-escrow-title">{t('escrow.title')}</h2>
      </div>

      {/* Quick nav pills */}
      <div className="bf-escrow-pills">
        <button type="button" onClick={() => topRef.current?.scrollIntoView({ behavior: 'smooth' })}>
          {t('escrow.pills.overview')}
        </button>
        <button type="button" onClick={() => s1Ref.current?.scrollIntoView({ behavior: 'smooth' })}>
          {t('escrow.pills.smooth')}
        </button>
        <button type="button" onClick={() => s2Ref.current?.scrollIntoView({ behavior: 'smooth' })}>
          {t('escrow.pills.no_confirm')}
        </button>
        <button type="button" onClick={() => s3Ref.current?.scrollIntoView({ behavior: 'smooth' })}>
          {t('escrow.pills.dispute')}
        </button>
        <button type="button" onClick={() => s4Ref.current?.scrollIntoView({ behavior: 'smooth' })}>
          {t('escrow.pills.auto_release')}
        </button>
      </div>

      {/* Scrollable content */}
      <div className="bf-escrow-scroll">
        <div className="bf-escrow-content">
          <section className="bf-escrow-card">
            <h3>{t('escrow.overview.title')}</h3>
            <p>
              <Trans
                i18nKey="escrow.overview.body"
                values={{
                  deliveryOptions: deliveryStr
                }}
                components={{ strong: <strong /> }}
              />
            </p>

            <div className="bf-escrow-def-list">
              <div>
                <h5>{t('escrow.defs.hold.title')}</h5>
                <p>{t('escrow.defs.hold.body')}</p>
              </div>
              <div>
                <h5>{t('escrow.defs.payout.title')}</h5>
                <p>
                  <Trans
                    i18nKey="escrow.defs.payout.body"
                    values={{ pct: ESCROW.SELLER_PAYOUT_PCT }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </div>
              <div>
                <h5>{t('escrow.defs.commission.title')}</h5>
                <p>
                  <Trans
                    i18nKey="escrow.defs.commission.body"
                    values={{ pct: ESCROW.COMMISSION_PCT }}
                    components={{ strong: <strong /> }}
                  />
                </p>
              </div>
            </div>

            <ul className="bf-escrow-keypoints">
              <li>
                <Trans
                  i18nKey="escrow.keypoints.seller_confirm"
                  values={{ hours: ESCROW.SELLER_CONFIRM_DEADLINE_HOURS }}
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  i18nKey="escrow.keypoints.timer_starts"
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>{t('escrow.keypoints.buyer_confirm_or_dispute')}</li>
              <li>
                <Trans i18nKey="escrow.keypoints.no_extensions" components={{ strong: <strong /> }} />
              </li>
              <li>{t('escrow.keypoints.locked_details')}</li>
            </ul>

            <div className="bf-escrow-hint">
              <Trans
                i18nKey="escrow.hint.chat_first"
                components={{ strong: <strong /> }}
              />
            </div>
          </section>

          {/* Scenario 1 */}
          <section className="bf-escrow-card" ref={s1Ref} id="smooth">
            <h3>{t('escrow.scenarios.smooth.title')}</h3>
            <ol>
              <li>{t('escrow.scenarios.smooth.steps.1')}</li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.smooth.steps.2"
                  values={{ hours: ESCROW.SELLER_CONFIRM_DEADLINE_HOURS, deliveryOptions: deliveryStr }}
                  components={{ strong: <strong />, em: <em /> }}
                />
              </li>
              <li>
                <Trans i18nKey="escrow.scenarios.smooth.steps.3" components={{ strong: <strong /> }} />
              </li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.smooth.steps.4"
                  values={{ payout: ESCROW.SELLER_PAYOUT_PCT, commission: ESCROW.COMMISSION_PCT }}
                  components={{ em: <em /> }}
                />
              </li>
            </ol>
          </section>

          {/* Scenario 2 */}
          <section className="bf-escrow-card" ref={s2Ref} id="noConfirm">
            <h3>{t('escrow.scenarios.no_confirm.title')}</h3>
            <ol>
              <li>{t('escrow.scenarios.no_confirm.steps.1')}</li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.no_confirm.steps.2"
                  values={{ hours: ESCROW.SELLER_CONFIRM_DEADLINE_HOURS }}
                />
              </li>
              <li>{t('escrow.scenarios.no_confirm.steps.3')}</li>
              <li>{t('escrow.scenarios.no_confirm.steps.4')}</li>
            </ol>
          </section>

          {/* Scenario 3 */}
          <section className="bf-escrow-card" ref={s3Ref} id="dispute">
            <h3>{t('escrow.scenarios.dispute.title')}</h3>
            <ol>
              <li>{t('escrow.scenarios.dispute.steps.1')}</li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.dispute.steps.2"
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>
                <ul>
                  <li>{t('escrow.scenarios.dispute.steps.3.buyer')}</li>
                  <li>{t('escrow.scenarios.dispute.steps.3.seller')}</li>
                </ul>
              </li>
              <li>{t('escrow.scenarios.dispute.steps.4')}</li>
            </ol>

            <div className="bf-escrow-hint">
              <Trans
                i18nKey="escrow.scenarios.dispute.tips.buyers"
                components={{ strong: <strong /> }}
                values={{ hours: 24 }}
              />
            </div>
            <div className="bf-escrow-hint">
              {t('escrow.scenarios.dispute.tips.evidence')}
            </div>
          </section>

          {/* Scenario 4 */}
          <section className="bf-escrow-card" ref={s4Ref} id="autoRelease">
            <h3>{t('escrow.scenarios.auto_release.title')}</h3>
            <ol>
              <li>{t('escrow.scenarios.auto_release.steps.1')}</li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.auto_release.steps.2"
                  components={{ strong: <strong /> }}
                />
              </li>
              <li>
                <Trans
                  i18nKey="escrow.scenarios.auto_release.steps.3"
                  values={{ payout: ESCROW.SELLER_PAYOUT_PCT, commission: ESCROW.COMMISSION_PCT }}
                />
              </li>
            </ol>
            <p className="bf-escrow-note">{t('escrow.scenarios.auto_release.note')}</p>
          </section>

          {/* FAQ */}
          <section className="bf-escrow-card">
            <h3>{t('escrow.faq.title')}</h3>

            <details>
              <summary>{t('escrow.faq.q1.q')}</summary>
              <p>
                <Trans
                  i18nKey="escrow.faq.q1.a"
                  values={{ hours: ESCROW.SELLER_CONFIRM_DEADLINE_HOURS }}
                />
              </p>
            </details>

            <details>
              <summary>{t('escrow.faq.q2.q')}</summary>
              <p>{t('escrow.faq.q2.a')}</p>
            </details>

            <details>
              <summary>{t('escrow.faq.q3.q')}</summary>
              <p>{t('escrow.faq.q3.a')}</p>
            </details>

            <details>
              <summary>{t('escrow.faq.q4.q')}</summary>
              <p>{t('escrow.faq.q4.a')}</p>
            </details>

            <details>
              <summary>{t('escrow.faq.q5.q')}</summary>
              <p>{t('escrow.faq.q5.a')}</p>
            </details>

            <details>
              <summary>{t('escrow.faq.q6.q')}</summary>
              <p>{t('escrow.faq.q6.a')}</p>
            </details>

            <details>
              <summary>{t('escrow.faq.q7.q')}</summary>
              <p>{t('escrow.faq.q7.a')}</p>
            </details>
          </section>

          <p className="bf-escrow-version">
            {t('escrow.version', { version: '0.5.0', author: 'Sizwe Eldwin Mondlane' })}
          </p>
        </div>
      </div>
    </div>
  );
}
